import React, { useState, useRef, useEffect } from "react";
import { isClient } from "@aspen/libs";

export interface DraggableFloatingIconProps {
  /**
   * 图标组件
   */
  icon: React.ReactNode;
  /**
   * 是否显示图标
   */
  visible?: boolean;
  /**
   * 点击事件
   */
  onClick?: () => void;
  /**
   * 右侧距离
   */
  right?: number;
  /**
   * 底部距离
   */
  bottom?: number;
}

/**
 * 可拖拽的悬浮图标组件
 */
export const DraggableFloatingIcon: React.FC<DraggableFloatingIconProps> = (
  props: DraggableFloatingIconProps,
) => {
  const { icon, onClick, right = 20, bottom = 50, visible = true } = props;
  const [isDragging, setIsDragging] = useState(false);
  const iconRef = useRef<HTMLDivElement>(null);

  // 更新初始位置
  const updateInitialPosition = element => {
    const maxRight = Math.max(0, window.innerWidth - (element.offsetWidth || 0));
    const maxBottom = Math.max(0, window.innerHeight - (element.offsetHeight || 0));

    element.style.right = `${Math.min(maxRight, right)}px`;
    element.style.bottom = `${Math.min(maxBottom, bottom)}px`;
  };

  // 使用useEffect设置初始位置和事件监听
  useEffect(() => {
    if (!isClient || !iconRef.current) return;

    const iconElement = iconRef.current;
    const initIconWidth = iconElement.offsetWidth;
    const initIconHeight = iconElement.offsetHeight;

    // 设置初始位置
    updateInitialPosition(iconElement);

    // 拖拽相关变量
    let startX = 0;
    let startY = 0;
    let startRight = 0;
    let startBottom = 0;

    // 窗口大小改变时更新位置
    const handleResize = () => {
      updateInitialPosition(iconElement);
    };
    window.addEventListener("resize", handleResize);

    // 鼠标按下事件
    const handleMouseDown = (e: MouseEvent) => {
      // 只有左键点击才触发拖拽
      if (e.button !== 0) return;

      e.preventDefault();
      e.stopPropagation();

      // 获取当前位置
      const computedStyle = window.getComputedStyle(iconElement);
      startRight = parseInt(computedStyle.right || "0", 10);
      startBottom = parseInt(computedStyle.bottom || "0", 10);

      // 记录鼠标位置
      startX = e.clientX;
      startY = e.clientY;

      // 设置拖拽状态
      setIsDragging(true);

      // 添加鼠标移动和释放事件
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    };

    // 鼠标移动事件
    const handleMouseMove = (e: MouseEvent) => {
      // 计算移动距离
      const dx = startX - e.clientX;
      const dy = startY - e.clientY;

      // 计算新位置，确保不会移出视口
      const newRight = Math.min(window.innerWidth - initIconWidth, Math.max(0, startRight + dx));
      const newBottom = Math.min(
        window.innerHeight - initIconHeight,
        Math.max(0, startBottom + dy),
      );
      // 设置新位置
      iconElement.style.right = `${newRight}px`;
      iconElement.style.bottom = `${newBottom}px`;
    };

    // 鼠标释放事件
    const handleMouseUp = () => {
      setIsDragging(false);

      // 移除事件监听
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };

    // 添加鼠标按下事件监听
    iconElement.addEventListener("mousedown", handleMouseDown);

    // 清理函数
    return () => {
      iconElement.removeEventListener("mousedown", handleMouseDown);
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      window.removeEventListener("resize", handleResize);
    };
  }, [right, bottom]);

  if (!isClient) {
    return null; // 服务端不渲染
  }

  // 客户端渲染
  return (
    <div
      ref={iconRef}
      className={`fixed rounded-full flex items-center justify-center overflow-hidden z-[9999] touch-none transition-all duration-300 ease-in-out ${
        isDragging ? "opacity-80" : ""
      } ${visible ? "visible" : "invisible"}`}
      style={{
        right: `${right}px`,
        bottom: `${bottom}px`,
        cursor: isDragging ? "grabbing" : "grab",
      }}
      onClick={isDragging ? undefined : onClick}>
      {icon}
    </div>
  );
};
